import { useEffect, useState } from 'react'
import type { JSX } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { Dialog, VisuallyHidden } from '@buffer-mono/popcorn'

import { useCommentsChannels } from '~publish/components/CommentsSidebar/useCommentsChannels'
import { getReturnSearchOnDialogClose } from '~publish/components/PostDialogs/helpers'

import { useMentionThread } from '../hooks/mentions/useMentionThread'
import { MentionDetailsContent } from './MentionDetailsContent'
import { MentionErrorState } from './MentionErrorState'
import type { MentionThreadData } from './types'

import styles from './MentionDetailsDialog.module.css'

type FetchState =
  | { status: 'loading' }
  | { status: 'loaded'; thread: MentionThreadData }
  | { status: 'error'; errorMessage: string }

/**
 * The single-mention details dialog, mounted on the community
 * `/mention/:mentionId` route. This is the landing surface for shared mention
 * links (see copyMentionLink.ts) — it opens over the Community page from a
 * pasted URL, showing the mention with its post, ancestors, and replies.
 */
export function MentionDetailsDialog(): JSX.Element | null {
  const navigate = useNavigate()
  const location = useLocation()
  const { mentionId } = useParams<{ mentionId: string }>()

  const [open, setOpen] = useState(true)
  const [fetchState, setFetchState] = useState<FetchState>({
    status: 'loading',
  })
  const { getMentionThread } = useMentionThread()
  const { channels, loading: channelsLoading } = useCommentsChannels()

  // Preserve the parent view's query state (filters, status) when
  // navigating back, dropping only dialog-scoped params.
  const returnSearch = getReturnSearchOnDialogClose(location.search)

  // A shared link carries no `tab`, so the page behind the dialog would
  // resolve to the Comments tab and closing would land there. Pinning the
  // Mentions tab renders the inbox behind the dialog and returns to it.
  const isOnMentionsTab =
    new URLSearchParams(location.search).get('tab') === 'mentions'
  useEffect(() => {
    if (isOnMentionsTab) return
    const params = new URLSearchParams(location.search)
    params.set('tab', 'mentions')
    navigate(
      { pathname: location.pathname, search: `?${params.toString()}` },
      { replace: true },
    )
  }, [isOnMentionsTab, location.pathname, location.search, navigate])

  useEffect(() => {
    if (mentionId === undefined) return

    // Reset when navigating between mention links (back/forward), so the
    // previous thread — and the Copy link's previous mention id — never
    // outlive their mention.
    setFetchState({ status: 'loading' })

    let cancelled = false
    getMentionThread(mentionId)
      .then((thread) => {
        if (cancelled) return undefined
        setFetchState({ status: 'loaded', thread })
        return undefined
      })
      .catch((error: unknown) => {
        if (cancelled) return undefined
        setFetchState({
          status: 'error',
          errorMessage:
            error instanceof Error ? error.message : 'Unknown error',
        })
        return undefined
      })

    return (): void => {
      cancelled = true
    }
  }, [mentionId, getMentionThread])

  const onOpenChange = (nextOpen: boolean): void => {
    setOpen(nextOpen)
    if (!nextOpen) {
      navigate(`..${returnSearch}`)
    }
  }

  if (fetchState.status === 'loading' || channelsLoading) {
    return null
  }

  const mention =
    fetchState.status === 'loaded' ? fetchState.thread.mention : null
  const channel = channels.find(({ id }) => id === mention?.channelId)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content size="large" className={styles.content}>
        <VisuallyHidden>
          <Dialog.Title>Mention details</Dialog.Title>
        </VisuallyHidden>
        <Dialog.CloseButton />
        <Dialog.Body className={styles.body}>
          {fetchState.status === 'error' ? (
            <MentionErrorState errorMessage={fetchState.errorMessage} />
          ) : mention === null || channel === undefined ? (
            <MentionErrorState />
          ) : (
            <MentionDetailsContent
              mention={mention}
              post={fetchState.thread.post}
              context={fetchState.thread.context}
              replies={fetchState.thread.replies}
              channel={channel}
            />
          )}
        </Dialog.Body>
      </Dialog.Content>
    </Dialog>
  )
}
